import { FC, Fragment, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { map, isEmpty } from "lodash";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Row,
  Divider,
  Loader,
  ErrorScreen,
  GENERIC_ERROR_TITLE,
} from "@/components";
import { useBoundStore, selectUserData } from "@/services/stores";
import { useRequestState } from "@/hooks";
import { Add } from "@/assets/icons";
import styles from "./styles.scss";
import {
  NO_VALUE,
  SUB_CONTRACTOR_PAGE_ROOT_PATH,
  SubcontractorPage,
} from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { requestSubcontractorsList, SubContractor } from "@/services/api";
import { UserSearch } from "@/pages/requests/components/users/components/table-filters";
import { useSearch } from "../../hooks/use-search";

export const SubcontractorsList: FC = () => {
  const { account } = useContext(AuthContext);
  const { user } = useBoundStore(selectUserData);

  const contractorId = user?.Contractor?.AdnocId;

  const {
    data: subContractors,
    setInProgress,
    setSuccess,
    isLoading,
    error,
    setError,
  } = useRequestState<SubContractor[]>({ isLoading: true });

  const { searchValue, setSearchValue, filteredSubContractors } =
    useSearch(subContractors);

  const navigate = useNavigate();

  useEffect(() => {
    if (contractorId) {
      setInProgress();
      const controller = new AbortController();

      requestSubcontractorsList(
        {
          email: account?.username ?? "",
        },
        {
          signal: controller.signal,
        },
      )
        .then(response => setSuccess(response))
        .catch(error => {
          setError({ message: error.message || GENERIC_ERROR_TITLE });
        });

      return () => {
        controller.abort();
      };
    }
  }, [setError, setSuccess, setInProgress, contractorId, account]);

  const handleEditSubcontractor = (id: number) =>
    navigate(
      `${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.Edit}/${id}`,
    );

  const handleAddSubcontractor = () =>
    navigate(`${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.Edit}`);

  if (error) {
    return (
      <div className={styles.noResultsContainer}>
        <ErrorScreen
          subtitle="Can't loading subcontractors list"
          error={error}
        />
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.searchContainer}>
        <UserSearch
          searchValue={searchValue}
          onSearch={setSearchValue}
          placeholder="Search for Subcontractor, Company ID, or Trade License"
        />
      </div>
      <div>
        <div className={styles.subcontractorsListHeader}>
          <div className={styles.subcontractorsListColumnLarge}>
            Subcontractor
          </div>
          <div className={styles.subcontractorsListColumnMedium}>
            Company ID
          </div>
          <div className={styles.subcontractorsListColumnMedium}>
            Trade License
          </div>
          <div className={styles.subcontractorsListColumnSmall} />
        </div>
      </div>
      {isLoading && (
        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      )}

      {!isLoading && isEmpty(filteredSubContractors) && (
        <div className={styles.noResultsContainer}>
          <div className={styles.errorMessage}>No subcontractors found</div>
        </div>
      )}

      {!isLoading &&
        map(filteredSubContractors, (item, i) => (
          <Fragment key={`subcontractor-list-item-${i}`}>
            <Row className={styles.subcontractorsListRow}>
              <div className={styles.subcontractorsListColumnLarge}>
                {item?.CompanyNameEn ?? NO_VALUE}
              </div>
              <div className={styles.subcontractorsListColumnMedium}>
                {item?.CompanyNo ?? NO_VALUE}
              </div>
              <div className={styles.subcontractorsListColumnMedium}>
                {item?.TradeLicenseNumber ?? NO_VALUE}
              </div>
              <div className={styles.subcontractorsListColumnSmall}>
                <Button
                  variant={ButtonVariant.Secondary}
                  size={ButtonSize.Small}
                  className={styles.editSubcontractorBtn}
                  onClick={() => handleEditSubcontractor(item?.SubContractorId)}
                >
                  Edit
                </Button>
              </div>
            </Row>
            <Divider />
          </Fragment>
        ))}

      <div className={styles.addSubcontractorBtnContainer}>
        <Button
          variant={ButtonVariant.Secondary}
          size={ButtonSize.Small}
          onClick={handleAddSubcontractor}
          disabled={isLoading || !!error}
          icon={<Add />}
        >
          Add Subcontractor
        </Button>
      </div>
    </div>
  );
};
